import { FolderBean, IMedia, type FilerSortingEnum } from "../fizapilib.auto.ts";
import * as FwDatas from "../../FwDatas.ts";
import * as i18n from "../../i18n.ts";
import { protoExtend } from "../../lib/ObjectUtil.ts";
import {BaseCache,SyncCache} from "../../lib/SyncCache.ts";
import Icons from "../../svg/Icons.ts";
import ISharableByMemberProto from "./ISharableByMember.ts";
import MultiplexApi from "../../server/MultiplexApi.ts"

type FolderContentItem = IMedia | FolderBean;

// Augmentation des types FolderBean pour les propriétés et méthodes ajoutées dynamiquement
declare module "../fizapilib.auto.ts" {
  interface FolderBean {
    // Properties added by ISharableByMemberProto
    sharingType?: 'all' | 'custom' | 'none';

    // Methods added via Object.assign
    sharedMembers(): any[];
    iconsimple(css: string): string;
    subLine(): string;
    getPath(): FolderBean[];
    isSubFolder() : boolean,
        
    // Dynamic properties added during tree construction
    folders?: FolderBean[];
    files?: IMedia[];
    content?: FolderContentItem[];
    parentFolder?: FolderBean;
  }
  interface IMedia {
    parentFolder?: FolderBean;
  }
  
}

/**
 * Extended FolderBean with tree structure
 */
export interface FolderRoot extends FolderBean {
  sharedFolders: FolderBean[];
  privateFolders: FolderBean[];
  getDefaultItem(): any;
}



const SORTER_BY_FILERSORTINGENUM: Partial<Record<FilerSortingEnum, (a: FolderContentItem, b: FolderContentItem) => number>> = {
  alphabetical(a: FolderContentItem, b: FolderContentItem): number {
    return a.name.localeCompare(b.name)
  },
  alphabeticalReverse(b: FolderContentItem, a: FolderContentItem): number {
    return a.name.localeCompare(b.name)
  },
  modificationDate(a: FolderContentItem, b: FolderContentItem): number {
    return a.filerModifDate.getTime() - b.filerModifDate.getTime();
  },
  modificationDateReverse (b: FolderContentItem, a: FolderContentItem): number {
    return a.filerModifDate.getTime() - b.filerModifDate.getTime();
  }
}


Object.assign(FolderBean.prototype,ISharableByMemberProto,{
  onDecode(this: FolderBean): void {
    if (this.parentFolderId===undefined){
      this.name = i18n.get('mainMenu.filer')
      this.emoji = ""
    }
    this.sharingType = this.sharedToAll?'all':(this.sharedMemberIds.length>1?'custom':'none');
  },
  isSubFolder(this: FolderBean){
    return this.parentFolder?.parentFolder!==undefined;
  },
  sharedMembers(this: FolderBean): any[] {
    return this.getPath()[1].sharedMemberIds.map((id: number)=>FwDatas.getProfile(id));
  },
  iconsimple(this: FolderBean, css: string): string {
    return `<icon style="${css};position:relative">
    <icon style="font-size:inherit">${Icons.filer.folder_colored.withColor(this.color)}</icon>
    <emoji style="font-size:50%;position:absolute;left:20%;top:20%;">${this.emoji}</emoji>
  </icon>`
  },
  subLine(this: FolderBean): string {
    if(this.folders.length===0){
      return i18n.get("files.foldercontent",this.files.length)
    }
    if(this.files.length===0){
      return i18n.get("launchpad.folder",this.folders.length)
    }
    return i18n.get("launchpad.folder",this.folders.length)+ " - " + i18n.get("files.foldercontent",this.files.length)


  },
  getPath(this: FolderBean): any[] {
    var
      limit = 20,
      result: any[] = [],
      crawler: any = this;

    do{
      result.push(crawler)
    }while((crawler = crawler.parentFolder)!==undefined && limit-->0)

    result.reverse()
    return result;
  }
})
class FilerTreeCacheClass extends BaseCache<FolderRoot> {
  cache: Record<string, FolderContentItem> = {};

  constructor(){
    super(async ()=>{
      var [files,folders] =  await Promise.all([FileCache.refresh(),FolderCache.refresh()]);
      return this.groupAll([files,folders])
    })
  }
  override invalidate(): void {
    super.invalidate();
    FileCache.invalidate();
    FolderCache.invalidate();
  }
  override kill(): void {
    super.kill();
    FileCache.kill();
    FolderCache.kill();
  }
  get(metaId: string): FolderContentItem {
    return this.cache[metaId]
  }
  getRoot(): FolderRoot {
    return this.datas
  }
  groupAll([files,folders]: [IMedia[], FolderBean[]]): FolderRoot {
    //console.log(arguments)
    var filesAndFolders: FolderContentItem[] = [...folders, ... files];
    this.cache = {};
    //cache all and initialize parentFolder
    filesAndFolders.forEach((item: FolderContentItem)=>this.cache[item.metaId]=item)
    filesAndFolders.forEach((item: FolderContentItem)=>{
      item.parentFolder = this.cache[item.parentFolderId] as FolderBean
    })
    //inititalize folder tree
    folders.forEach((item: any)=>{
      item.folders = []
      item.files = []
    })
    //assign child in parent
    folders.forEach((folder: FolderBean)=>{
      folder.parentFolder?.folders.push(folder)
    })
    files.forEach((file: IMedia)=>{
      file.parentFolder?.files.push(file)
    })
    //assemble and order
    folders.forEach((folder: FolderBean)=>{
      folder.folders.sort(SORTER_BY_FILERSORTINGENUM[folder.filerSorting]);
      folder.content = [... folder.folders , ... folder.files]
      folder.content.sort(SORTER_BY_FILERSORTINGENUM[folder.filerSorting]);
    })
    var root = folders.find((item: FolderBean)=>item.parentFolderId===undefined);
    folders.forEach((folder: FolderBean)=>{
      if(folder.parentFolderId === root.metaId){
        propagateSharingType(folder)
      }
    })
    return protoExtend(root,{
      sharedFolders:root.folders.filter((item: FolderBean)=>item.sharedToAll || item.sharedMemberIds.length>1),
      privateFolders:root.folders.filter((item: FolderBean)=>!(item.sharedToAll || item.sharedMemberIds.length>1)),
      getDefaultItem(): any {
        return root.folders[0]||{metaId:'nope'}
      },
      find(predicate: (item: any) => boolean): FolderBean {
        //this method is called for whatever filer navigation so we check all folders
        return folders.find(predicate);
      }
    })
  }
}
function propagateSharingType(folder: FolderBean): void {
  folder.folders.forEach((sub: FolderBean)=>{
    sub.sharingType = folder.sharingType
    propagateSharingType(sub)
  })
}



export const
  FileCache = new SyncCache<IMedia>((request)=>MultiplexApi.filersyncfile(request.syncToken)),
  FolderCache = new SyncCache<FolderBean>((request)=>MultiplexApi.filersyncfolder(request.syncToken)),
  FilerTreeCache = new FilerTreeCacheClass();
